import ClearIcon from '@mui/icons-material/Clear';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import FilterListIcon from '@mui/icons-material/FilterList';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState, useMemo, useRef, memo } from 'react';
import { getLocalStorage } from '../../utils/safeJson';
import {
  leadPriorities,
  leadTypes,
  newStatuses,
  sizeBuckets,
  types,
  strategicAccountOptions,
  statusStages,
  getOrderedStages,
  getStatusesForStages
} from '../../constants/constants';
import { ENGAGEMENT_STRATEGIES } from '../../constants/engagement';

// Local components
import FilterGroup from './FilterGroup';
import RecencyFilters from './RecencyFilters';
import FlagFilters from './FlagFilters';
import SavedViewsControl from './SavedViewsControl';
import useSavedViews from './useSavedViews';
import { getStatusColor, EMPTY_SET, getDefaultFilterState } from './constants';

/**
 * FilterBar - Standard filter bar component for associations
 * Props:
 *  - value: { statuses:Set, types:Set, strategies:Set, text:string }
 *  - onChange(newValue)
 *  - disableClosedLost (bool) default true
 *  - savedViewsKey (string) localStorage key for saved views
 */
const FilterBar = memo(function FilterBar({
  value,
  onChange,
  disableClosedLost = true,
  onCommand,
  autoCollapseOnSearch = true,
  savedViewsKey = 'associationsSavedViews',
}) {
  try {
    // Theme for dark mode support
    const theme = useTheme();

    // Provide safe defaults
    const safeValue = useMemo(() => {
      if (!value) return getDefaultFilterState();
      return {
        statuses: value.statuses || EMPTY_SET,
        leadTypes: value.leadTypes || EMPTY_SET,
        strategies: value.strategies || EMPTY_SET,
        selectedStages: value.selectedStages || EMPTY_SET,
        funnelStages: value.funnelStages || EMPTY_SET,
        strategicAccounts: value.strategicAccounts || EMPTY_SET,
        text: value.text || '',
        lastUpdatedDays: value.lastUpdatedDays,
        lastEngagedDays: value.lastEngagedDays,
        lastCreatedDays: value.lastCreatedDays,
        sizeBuckets: value.sizeBuckets || EMPTY_SET,
        duplicatesOnly: !!value.duplicatesOnly,
        legacyLeadsOnly: !!value.legacyLeadsOnly,
        associationTypes: value.associationTypes || EMPTY_SET,
        selfManaged: value.selfManaged || EMPTY_SET,
        stalledOnly: !!value.stalledOnly,
      };
    }, [value]);

    const safeOnChange = useMemo(
      () => onChange || (() => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('FilterBar: onChange not provided');
        }
      }),
      [onChange]
    );

    const [search, setSearch] = useState(safeValue?.text || '');
    const searchInputRef = useRef(null);
    const [advancedOpen, setAdvancedOpen] = useState(false);

    // Saved views hook
    const savedViewsState = useSavedViews(savedViewsKey, safeOnChange);

    // Keyboard navigation: '/' key focuses search input
    useEffect(() => {
      const handleKeyPress = (e) => {
        if (e.key === '/' && !e.target.matches('input, textarea, [contenteditable]')) {
          e.preventDefault();
          searchInputRef.current?.focus();
        }
      };
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }, []);

    const persistUi = next => {
      try {
        localStorage.setItem('globalFilterBarUi', JSON.stringify({ advancedOpen: next }));
      } catch (err) {
        console.warn('persistUi failed', err);
      }
    };

    useEffect(() => {
      const parsed = getLocalStorage('globalFilterBarUi', { advancedOpen: false });
      if (parsed.advancedOpen !== true) {
        persistUi(false);
      }
    }, []);

    const statusKeys = Object.keys(newStatuses);
    const associationTypes = Object.keys(types || {});
    const selfManagedOptions = ['Yes', 'No', 'Blended', 'Unknown'];

    // Keep internal search in sync with parent
    useEffect(() => {
      const parentText = value?.text || '';
      setSearch(prev => (prev === parentText ? prev : parentText));
    }, [value?.text]);

    // Debounce search propagate
    useEffect(() => {
      if (search === safeValue.text) return;
      const h = setTimeout(() => {
        safeOnChange({ ...safeValue, text: search });
      }, 250);
      return () => clearTimeout(h);
    }, [search, safeOnChange, safeValue]);

    const toggle = (group, key) => {
      if (group === 'selectedStages') {
        const currentStages = new Set(safeValue.selectedStages);
        const currentStatuses = new Set(safeValue.statuses);
        const stage = statusStages[key];

        if (currentStages.has(key)) {
          currentStages.delete(key);
          stage.statuses.forEach(status => currentStatuses.delete(status));
        } else {
          currentStages.add(key);
          stage.statuses.forEach(status => currentStatuses.add(status));
        }

        safeOnChange({
          ...safeValue,
          selectedStages: currentStages,
          statuses: currentStatuses
        });
        return;
      }

      const current = new Set(safeValue?.[group]);
      if (current.has(key)) current.delete(key);
      else current.add(key);
      safeOnChange({ ...safeValue, [group]: current });
    };

    const setAll = (group, keys) => safeOnChange({ ...safeValue, [group]: new Set(keys) });
    const clearAll = group => {
      if (group === 'leadTypes') {
        safeOnChange({ ...safeValue, [group]: new Set(['New Customer']) });
      } else {
        safeOnChange({ ...safeValue, [group]: new Set() });
      }
    };

    const setLastUpdatedDays = days => safeOnChange({ ...safeValue, lastUpdatedDays: days });
    const setLastEngagedDays = days => safeOnChange({ ...safeValue, lastEngagedDays: days });
    const setLastCreatedDays = days => safeOnChange({ ...safeValue, lastCreatedDays: days });

    // Reset filters to defaults
    const defaultStatuses = disableClosedLost
      ? statusKeys.filter(s => s !== 'Closed Lost')
      : statusKeys;

    const resetFilters = () => {
      setSearch('');
      onChange({
        ...value,
        associationTypes: new Set(),
        selfManaged: new Set(),
        statuses: new Set(defaultStatuses),
        leadTypes: new Set(['New Customer']),
        strategies: new Set(),
        selectedStages: new Set(),
        sizeBuckets: new Set(),
        text: '',
        lastUpdatedDays: null,
        lastEngagedDays: null,
        lastCreatedDays: null,
        stalledOnly: false,
        duplicatesOnly: false,
        legacyLeadsOnly: false,
      });
    };

    const hasActiveFilters =
      (safeValue.statuses && safeValue.statuses.size !== defaultStatuses.length) ||
      (safeValue.leadTypes && safeValue.leadTypes.size > 0) ||
      (safeValue.strategies && safeValue.strategies.size > 0) ||
      (safeValue.sizeBuckets && safeValue.sizeBuckets.size > 0) ||
      safeValue.duplicatesOnly ||
      safeValue.legacyLeadsOnly ||
      safeValue.stalledOnly ||
      !!safeValue.text ||
      safeValue.lastUpdatedDays != null ||
      safeValue.lastEngagedDays != null ||
      safeValue.lastCreatedDays != null;

    const handleSearchKey = e => {
      if (e.key === 'Enter') {
        const raw = e.target.value.trim();
        if (onCommand && (raw.startsWith('!') || raw.startsWith('>'))) {
          onCommand(raw);
          setSearch('');
        }
        if (autoCollapseOnSearch && advancedOpen) {
          const next = false;
          setAdvancedOpen(next);
          persistUi(next);
        }
        try {
          e.currentTarget.blur();
        } catch (err) {
          console.warn('blur failed', err);
        }
      }
    };

    // Render selected chips for compact view
    const renderSelectedChips = () => {
      const chips = [];
      if (safeValue.statuses?.size > 0 && safeValue.statuses.size !== statusKeys.length) {
        safeValue.statuses.forEach(s =>
          chips.push({
            key: `status-${s}`,
            label: s,
            color: getStatusColor(s),
            onDelete: () => toggle('statuses', s),
          })
        );
      }
      if (safeValue.leadTypes?.size) {
        safeValue.leadTypes.forEach(t =>
          chips.push({ key: `lead-${t}`, label: t, onDelete: () => toggle('leadTypes', t) })
        );
      }
      if (safeValue.strategies?.size) {
        safeValue.strategies.forEach(t =>
          chips.push({ key: `strategy-${t}`, label: t, onDelete: () => toggle('strategies', t) })
        );
      }
      if (safeValue.sizeBuckets?.size) {
        safeValue.sizeBuckets.forEach(t =>
          chips.push({ key: `size-${t}`, label: t, onDelete: () => toggle('sizeBuckets', t) })
        );
      }
      if (safeValue.associationTypes?.size) {
        safeValue.associationTypes.forEach(t =>
          chips.push({ key: `atype-${t}`, label: `Type: ${t}`, onDelete: () => toggle('associationTypes', t) })
        );
      }
      if (value?.selfManaged?.size) {
        value.selfManaged.forEach(t =>
          chips.push({ key: `sm-${t}`, label: `Self-Managed: ${t}`, onDelete: () => toggle('selfManaged', t) })
        );
      }
      if (value?.lastUpdatedDays != null)
        chips.push({ key: 'upd', label: `Updated ≤ ${value.lastUpdatedDays}d`, onDelete: () => setLastUpdatedDays(null) });
      if (value?.lastEngagedDays != null)
        chips.push({ key: 'eng', label: `Engaged ≤ ${value.lastEngagedDays}d`, onDelete: () => setLastEngagedDays(null) });
      if (value?.lastCreatedDays != null)
        chips.push({ key: 'created', label: `Created ≤ ${value.lastCreatedDays}d`, onDelete: () => setLastCreatedDays(null) });
      if (value?.duplicatesOnly)
        chips.push({ key: 'dups', label: 'Duplicates', color: '#ed6c02', onDelete: () => onChange({ ...value, duplicatesOnly: false }) });
      if (value?.legacyLeadsOnly)
        chips.push({ key: 'legacy', label: 'Legacy Leads', color: '#1976d2', onDelete: () => onChange({ ...value, legacyLeadsOnly: false }) });
      if (value?.stalledOnly)
        chips.push({ key: 'stalled', label: 'Stalled', color: '#6d4c41', onDelete: () => onChange({ ...value, stalledOnly: false }) });
      return chips;
    };

    const selectedChips = renderSelectedChips();

    return (
      <Box
        sx={{
          border: '1px solid #dadada',
          borderRadius: 1.5,
          p: 1,
          mb: 2,
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Compact header row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <SavedViewsControl
            savedViews={savedViewsState.savedViews}
            activeViewName={savedViewsState.activeViewName}
            setActiveViewName={savedViewsState.setActiveViewName}
            loadView={savedViewsState.loadView}
            saveCurrentView={() => savedViewsState.saveCurrentView(safeValue)}
            deleteView={savedViewsState.deleteView}
            confirmDelete={savedViewsState.confirmDelete}
            handleConfirmDelete={savedViewsState.handleConfirmDelete}
            handleCancelDelete={savedViewsState.handleCancelDelete}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, minHeight: 34 }}>
            {selectedChips.length === 0 && (
              <Chip size='small' label='All Records' variant='outlined' sx={{ opacity: 0.6 }} />
            )}
            {selectedChips.map(c => (
              <Chip
                key={c.key}
                size='small'
                label={c.label}
                onDelete={c.onDelete}
                sx={{
                  bgcolor: c.color || undefined,
                  color: c.color ? '#fff' : undefined,
                  '& .MuiChip-deleteIcon': { color: c.color ? '#fff' : 'inherit' },
                }}
              />
            ))}
          </Box>
          <TextField
            ref={searchInputRef}
            id="filter-search-field"
            name="filterSearch"
            size='small'
            placeholder='Search or !note / >Status (Press / to focus)'
            value={search}
            onKeyDown={handleSearchKey}
            onChange={e => setSearch(e.target.value)}
            sx={{ minWidth: 220, '& .MuiInputBase-root': { height: 34, fontSize: 13 } }}
            InputProps={{
              endAdornment: search ? (
                <InputAdornment position='end'>
                  <IconButton size='small' onClick={() => setSearch('')} aria-label='clear search'>
                    <ClearIcon fontSize='small' />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {hasActiveFilters && (
            <Button size='small' variant='text' onClick={resetFilters} sx={{ textTransform: 'none' }}>
              Clear All
            </Button>
          )}
          <Button
            size='small'
            startIcon={advancedOpen ? <CloseFullscreenIcon /> : <FilterListIcon />}
            variant={advancedOpen ? 'contained' : 'outlined'}
            color='primary'
            onClick={() => {
              const next = !advancedOpen;
              setAdvancedOpen(next);
              persistUi(next);
            }}
            sx={{ color: advancedOpen ? 'white' : undefined }}
          >
            {advancedOpen ? 'Hide Advanced' : 'Advanced Filters'}
          </Button>
        </Box>

        {/* Advanced filters panel */}
        {advancedOpen && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'flex-start',
              pt: 0.5,
              background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f3f6fa',
              borderRadius: 1,
              p: 1,
            }}
          >
            <FilterGroup
              label='Pipeline Stages'
              groupKey='selectedStages'
              keys={getOrderedStages().map(s => s.key)}
              value={safeValue}
              setAll={setAll}
              clearAll={clearAll}
              toggle={toggle}
              disableClosedLost={disableClosedLost}
              renderChip={(stageKey, isSelected, onClick) => {
                const stage = statusStages[stageKey];
                return (
                  <Chip
                    key={stageKey}
                    size='small'
                    label={stage.name}
                    onClick={onClick}
                    variant={isSelected ? 'filled' : 'outlined'}
                    sx={{
                      bgcolor: isSelected ? stage.color : 'transparent',
                      borderColor: stage.color,
                      color: isSelected ? '#fff' : stage.color,
                      border: '1.5px solid',
                      fontWeight: 600,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        bgcolor: stage.hoverColor,
                        borderColor: stage.hoverColor,
                        color: '#fff',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      },
                    }}
                  />
                );
              }}
            />
            <FilterGroup
              label='Statuses'
              groupKey='statuses'
              keys={statusKeys}
              value={safeValue}
              setAll={setAll}
              clearAll={clearAll}
              toggle={toggle}
              disableClosedLost={disableClosedLost}
              renderChip={(k, isSelected, onClick) => (
                <Chip
                  key={k}
                  size='small'
                  label={k}
                  onClick={onClick}
                  variant={isSelected ? 'filled' : 'outlined'}
                  disabled={disableClosedLost && k === 'Closed Lost'}
                  sx={{
                    bgcolor: isSelected ? getStatusColor(k) : 'transparent',
                    borderColor: getStatusColor(k),
                    color: isSelected ? '#fff' : 'inherit',
                    border: '1px solid',
                    '&:hover': { bgcolor: getStatusColor(k), color: '#fff' },
                  }}
                />
              )}
            />
            <FilterGroup
              label='Lead Types'
              groupKey='leadTypes'
              keys={leadTypes}
              value={safeValue}
              setAll={setAll}
              clearAll={clearAll}
              toggle={toggle}
              disableClosedLost={disableClosedLost}
            />
            <FilterGroup
              label='Lead Priority'
              groupKey='leadPriorities'
              keys={leadPriorities}
              value={safeValue}
              setAll={setAll}
              clearAll={clearAll}
              toggle={toggle}
              disableClosedLost={disableClosedLost}
            />
            <FilterGroup
              label='Strategies'
              groupKey='strategies'
              keys={ENGAGEMENT_STRATEGIES}
              value={safeValue}
              setAll={setAll}
              clearAll={clearAll}
              toggle={toggle}
              disableClosedLost={disableClosedLost}
            />
            <FilterGroup
              label='Size Buckets'
              groupKey='sizeBuckets'
              keys={sizeBuckets}
              value={safeValue}
              setAll={setAll}
              clearAll={clearAll}
              toggle={toggle}
              disableClosedLost={disableClosedLost}
            />
            {associationTypes.length > 0 && (
              <FilterGroup
                label='Association Types'
                groupKey='associationTypes'
                keys={associationTypes}
                value={safeValue}
                setAll={setAll}
                clearAll={clearAll}
                toggle={toggle}
                disableClosedLost={disableClosedLost}
              />
            )}
            <FilterGroup
              label='Self-Managed'
              groupKey='selfManaged'
              keys={selfManagedOptions}
              value={safeValue}
              setAll={setAll}
              clearAll={clearAll}
              toggle={toggle}
              disableClosedLost={disableClosedLost}
            />
            <FilterGroup
              label='Strategic Account'
              groupKey='strategicAccounts'
              keys={strategicAccountOptions}
              value={safeValue}
              setAll={setAll}
              clearAll={clearAll}
              toggle={toggle}
              disableClosedLost={disableClosedLost}
              renderChip={(k, isSelected, onClick) => (
                <Chip
                  key={k}
                  size='small'
                  label={k === 'Yes' ? '⭐ Strategic' : 'Standard'}
                  onClick={onClick}
                  variant={isSelected ? 'filled' : 'outlined'}
                  sx={{
                    bgcolor: isSelected ? (k === 'Yes' ? '#FFD700' : theme.palette.action.selected) : 'transparent',
                    borderColor: k === 'Yes' ? '#FFD700' : '#e0e0e0',
                    color: isSelected ? (k === 'Yes' ? '#000' : 'inherit') : 'inherit',
                    border: '1px solid',
                    '&:hover': {
                      bgcolor: k === 'Yes' ? '#FFD700' : theme.palette.action.hover,
                      color: k === 'Yes' ? '#000' : 'inherit'
                    },
                  }}
                />
              )}
            />
            <RecencyFilters
              lastUpdatedDays={value?.lastUpdatedDays}
              lastEngagedDays={value?.lastEngagedDays}
              lastCreatedDays={value?.lastCreatedDays}
              onLastUpdatedChange={setLastUpdatedDays}
              onLastEngagedChange={setLastEngagedDays}
              onLastCreatedChange={setLastCreatedDays}
            />
            <FlagFilters
              duplicatesOnly={value?.duplicatesOnly}
              legacyLeadsOnly={value?.legacyLeadsOnly}
              stalledOnly={value?.stalledOnly}
              onDuplicatesChange={() => onChange({ ...value, duplicatesOnly: !value?.duplicatesOnly })}
              onLegacyChange={() => onChange({ ...value, legacyLeadsOnly: !value?.legacyLeadsOnly })}
              onStalledChange={() => onChange({ ...value, stalledOnly: !value?.stalledOnly })}
            />
          </Box>
        )}
      </Box>
    );
  } catch (error) {
    console.error('FilterBar component error:', error.message);
    throw error;
  }
});

export default FilterBar;
